import { useState } from "react";
import PropTypes from "prop-types";
import ReactSelect from "react-select";
import { useQuery } from "react-query";
import styled from "styled-components";
import { Button } from "../../Button";
import { QueryLoader } from "../../Loader";
import { theme } from "../../../styles/theme";
import { useDecisionContext } from "../../../context/decisionContext";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useDecisionAccess from "../../../hooks/useDecisionAccess";
import {
	addDecisionCollaborator,
	addDecisionReader,
	addDecisionReaderUnit,
	getOrganizationUnits,
	getOrganizationUsers,
	removeDecisionCollaborator,
	removeDecisionReader,
	removeDecisionReaderUnit,
	setDecisionOwner,
} from "../../../api/decision/access";

const StyledAccess = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2.4rem;
	padding: 2.4rem 0;
	font-family: ${theme.font.secondary};

	section h4 {
		margin: 0 0 0.4rem;
	}

	section > p {
		margin: 0 0 1.2rem;
		color: ${theme.color.gray};
	}

	ul {
		list-style: none;
		margin: 0 0 1.2rem;
		padding: 0;
	}

	li {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1.2rem;
		padding: 0.8rem 1.2rem;
		border-bottom: 1px solid ${theme.color.blue1};
	}

	li small {
		color: ${theme.color.gray};
		margin-left: 0.8rem;
	}

	.empty {
		color: ${theme.color.gray2};
	}

	.add {
		display: flex;
		align-items: center;
		gap: 1.2rem;
		max-width: 56rem;
	}

	.add > div:first-child {
		flex: 1;
	}

	.notice {
		padding: 1.2rem;
		background: ${theme.color.blue0};
		border-radius: 3px;
	}
`;

const selectStyles = {
	control: (base) => ({ ...base, minHeight: 36, borderRadius: 3, border: `1px solid ${theme.color.blue1}` }),
	menu: (base) => ({ ...base, zIndex: 9999 }),
};

const fullName = (user) => `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() || user.email || `#${user.id}`;

function AddRow({ options, placeholder, buttonLabel, onAdd }) {
	const [selected, setSelected] = useState(null);
	const [busy, setBusy] = useState(false);

	const add = async () => {
		if (!selected) return;
		setBusy(true);
		try {
			await onAdd(selected.value);
			setSelected(null);
		} catch (e) {
			// The request helper already showed the error
		} finally {
			setBusy(false);
		}
	};

	return (
		<div className="add">
			<ReactSelect
				options={options}
				value={selected}
				onChange={setSelected}
				placeholder={placeholder}
				styles={selectStyles}
				isClearable
			/>
			<Button small onClick={add} disabled={!selected || busy}>
				{buttonLabel}
			</Button>
		</div>
	);
}

AddRow.propTypes = {
	options: PropTypes.array.isRequired,
	placeholder: PropTypes.string,
	buttonLabel: PropTypes.string,
	onAdd: PropTypes.func.isRequired,
};

function PeopleList({ people, emptyText, canRemove, onRemove }) {
	if (!people.length) {
		return <p className="empty">{emptyText}</p>;
	}
	return (
		<ul>
			{people.map((person) => (
				<li key={person.id}>
					<span>
						{fullName(person)}
						{person.email && <small>{person.email}</small>}
					</span>
					{canRemove && (
						<Button small bg={false} onClick={() => onRemove(person.id).catch(() => { })}>
							Remove
						</Button>
					)}
				</li>
			))}
		</ul>
	);
}

PeopleList.propTypes = {
	people: PropTypes.array.isRequired,
	emptyText: PropTypes.string,
	canRemove: PropTypes.bool,
	onRemove: PropTypes.func,
};

/**
 * Access tab: the owner, collaborators and readers. Only the owner changes access.
 */
export default function DecisionAccess({ newDecision = false }) {
	const { id } = useDecisionContext() || {};
	const { axiosPrivate } = useAxiosPrivate();
	const { access, permissions, isLoading, isError, refreshDecision } = useDecisionAccess(newDecision ? null : id);
	const canManage = Boolean(permissions.manageAccess);

	const { data: users = [] } = useQuery("organization-users", () => getOrganizationUsers(axiosPrivate), { enabled: canManage });
	const { data: units = [] } = useQuery("organization-units-flat", () => getOrganizationUnits(axiosPrivate), { enabled: canManage });
	const [handOverTo, setHandOverTo] = useState(null);

	if (newDecision) {
		return (
			<StyledAccess>
				<p className="notice">Save the decision first. You will be its owner and can then invite collaborators and readers.</p>
			</StyledAccess>
		);
	}
	if (isLoading) {
		return <QueryLoader />;
	}
	if (isError || !access) {
		return <div>Access could not be loaded.</div>;
	}

	const run = (request) => request.then(refreshDecision);
	const ownerId = access.owner?.id;
	const collaboratorIds = access.collaborators.map((user) => user.id);
	const readerIds = access.readerUsers.map((user) => user.id);
	const readerUnitIds = access.readerUnits.map((unit) => unit.id);
	const userOptions = (excluded) =>
		users.filter((user) => !excluded.includes(user.id)).map((user) => ({ value: user.id, label: fullName(user) }));

	return (
		<StyledAccess>
			{!canManage && <p className="notice">Only the owner can change who has access to this decision.</p>}

			<section>
				<h4>Owner</h4>
				<p>Manages access and can delete the decision. Can hand it over to anyone in the organization.</p>
				<ul>
					<li>
						<span>
							{access.owner ? fullName(access.owner) : "No owner"}
							{access.owner?.email && <small>{access.owner.email}</small>}
						</span>
					</li>
				</ul>
				{canManage &&
					(handOverTo ? (
						<div className="add">
							<span>
								Hand over to <strong>{handOverTo.label}</strong>? You will stay on as a collaborator, and only the new
								owner can change access afterwards.
							</span>
							<Button
								small
								onClick={() =>
									run(setDecisionOwner(axiosPrivate, id, handOverTo.value))
										.then(() => setHandOverTo(null))
										.catch(() => { })
								}
							>
								Hand over
							</Button>
							<Button small bg={false} onClick={() => setHandOverTo(null)}>
								Cancel
							</Button>
						</div>
					) : (
						<AddRow
							options={userOptions([ownerId])}
							placeholder="Choose a new owner"
							buttonLabel="Hand over"
							onAdd={async (userId) => setHandOverTo(userOptions([ownerId]).find((option) => option.value === userId))}
						/>
					))}
			</section>

			<section>
				<h4>Collaborators</h4>
				<p>Work on the decision at every status: edit the content while it is a draft or ongoing, and change its status.</p>
				<PeopleList
					people={access.collaborators}
					emptyText="No collaborators"
					canRemove={canManage}
					onRemove={(userId) => run(removeDecisionCollaborator(axiosPrivate, id, userId))}
				/>
				{canManage && (
					<AddRow
						options={userOptions([ownerId, ...collaboratorIds])}
						placeholder="Choose a person"
						buttonLabel="Add collaborator"
						onAdd={(userId) => run(addDecisionCollaborator(axiosPrivate, id, userId))}
					/>
				)}
			</section>

			<section>
				<h4>Readers</h4>
				<p>See the decision once it is done. Members of a unit get access while they are in the unit.</p>
				<PeopleList
					people={access.readerUsers}
					emptyText="No individual readers"
					canRemove={canManage}
					onRemove={(userId) => run(removeDecisionReader(axiosPrivate, id, userId))}
				/>
				{canManage && (
					<AddRow
						options={userOptions([ownerId, ...collaboratorIds, ...readerIds])}
						placeholder="Choose a person"
						buttonLabel="Add reader"
						onAdd={(userId) => run(addDecisionReader(axiosPrivate, id, userId))}
					/>
				)}
				<h4 style={{ marginTop: "2.4rem" }}>Reader units</h4>
				<PeopleList
					people={access.readerUnits.map((unit) => ({ id: unit.id, firstName: unit.name }))}
					emptyText="No reader units"
					canRemove={canManage}
					onRemove={(unitId) => run(removeDecisionReaderUnit(axiosPrivate, id, unitId))}
				/>
				{canManage && (
					<AddRow
						options={units.filter((unit) => !readerUnitIds.includes(unit.id)).map((unit) => ({ value: unit.id, label: unit.name }))}
						placeholder="Choose a unit"
						buttonLabel="Add unit"
						onAdd={(unitId) => run(addDecisionReaderUnit(axiosPrivate, id, unitId))}
					/>
				)}
			</section>
		</StyledAccess>
	);
}

DecisionAccess.propTypes = {
	newDecision: PropTypes.bool,
};
